import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  Progress,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiDownload, FiSearch, FiTrash2 } from 'react-icons/fi';
import firebaseFirestore from '~/config/firebase/firestore';
import { useAuth } from '~/hooks/useAuth';

/**
 * Limpieza de los negocios que ya no operan.
 *
 * Borra de la base de datos las empresas inactivas con todo lo suyo —sedes,
 * reservas y formularios— y deja intacto lo que está activo.
 *
 * Cómo evita hacer daño:
 *  · solo entra el superadministrador (se comprueba por el nombre del rol);
 *  · **nunca** toca una empresa con `status: 'active'`;
 *  · no toca las reservas cuya empresa no se puede identificar;
 *  · obliga a descargar el respaldo antes de habilitar el borrado;
 *  · exige escribir la palabra de confirmación;
 *  · vuelve a contar al terminar para demostrar qué quedó.
 *
 * Y por encima de todo esto, Firestore guarda 7 días de recuperación continua:
 * se puede volver a cualquier instante anterior al borrado.
 */

const PALABRA = 'BORRAR';

type Empresa = { id: string; nombre: string; estado: string };
type Analisis = {
  conservadas: Empresa[];
  aBorrar: Array<Empresa & { sedes: number; reservas: number; formularios: number }>;
  totales: { sedes: number; reservas: number; formularios: number };
  sinEmpresa: number;
  docs: {
    companies: string[];
    locations: string[];
    reservations: string[];
    forms: string[];
  };
  respaldo: unknown;
};

/** Id de empresa venga como texto o como referencia de Firestore. */
const idEmpresa = (valor: unknown): string => {
  if (!valor) return '';
  if (typeof valor === 'string') return valor;
  const ref = valor as { id?: string; path?: string };
  return ref.id ?? ref.path?.split('/').pop() ?? '';
};

/** Convierte fechas y referencias a algo que se pueda guardar en un JSON. */
const serializable = (valor: unknown): unknown =>
  JSON.parse(
    JSON.stringify(valor, (_clave, v) => {
      if (v && typeof v === 'object') {
        if (typeof (v as { toDate?: unknown }).toDate === 'function') {
          return { __fecha__: (v as { toDate: () => Date }).toDate().toISOString() };
        }
        if ((v as { path?: string }).path && (v as { id?: string }).id) {
          return { __referencia__: (v as { path: string }).path };
        }
      }
      return v;
    })
  );

const LimpiarNegocios = () => {
  const { user } = useAuth();
  const esSuperadmin = user?.userRole?.name === 'superadmin';

  const [analizando, setAnalizando] = useState(false);
  const [analisis, setAnalisis] = useState<Analisis | null>(null);
  const [descargado, setDescargado] = useState(false);
  const [palabra, setPalabra] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [progreso, setProgreso] = useState({ hechos: 0, total: 0, paso: '' });
  const [resultado, setResultado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analizar = async () => {
    setError(null);
    setResultado(null);
    setDescargado(false);
    setAnalizando(true);
    try {
      const [emp, loc, res, frm] = await Promise.all([
        getDocs(collection(firebaseFirestore, 'companies')),
        getDocs(collection(firebaseFirestore, 'locations')),
        getDocs(collection(firebaseFirestore, 'reservations')),
        getDocs(collection(firebaseFirestore, 'forms')),
      ]);

      const empresas = emp.docs.map((d) => ({
        id: d.id,
        nombre: (d.data().name as string) ?? d.id,
        estado: (d.data().status as string) ?? 'sin estado',
        datos: d.data(),
      }));
      // Regla de oro: lo activo no se toca, pase lo que pase.
      const conservadas = empresas.filter((c) => c.estado === 'active');
      const candidatas = empresas.filter((c) => c.estado !== 'active');
      if (conservadas.length === 0) {
        throw new Error('No hay ninguna empresa activa. Se cancela por seguridad.');
      }
      const ids = new Set(candidatas.map((c) => c.id));

      const deEmpresa = (d: QueryDocumentSnapshot<DocumentData>, campo: 'company' | 'location') =>
        campo === 'company'
          ? idEmpresa(d.data().company)
          : idEmpresa((d.data().location as { company?: unknown })?.company);

      const locsBorrar = loc.docs.filter((d) => ids.has(deEmpresa(d, 'company')));
      const resBorrar = res.docs.filter((d) => ids.has(deEmpresa(d, 'location')));
      const frmBorrar = frm.docs.filter((d) => ids.has(deEmpresa(d, 'company')));
      const sinEmpresa = res.docs.filter((d) => !deEmpresa(d, 'location')).length;

      const detalle = candidatas.map((c) => ({
        id: c.id,
        nombre: c.nombre,
        estado: c.estado,
        sedes: locsBorrar.filter((d) => idEmpresa(d.data().company) === c.id).length,
        reservas: resBorrar.filter(
          (d) => idEmpresa((d.data().location as { company?: unknown })?.company) === c.id
        ).length,
        formularios: frmBorrar.filter((d) => idEmpresa(d.data().company) === c.id).length,
      }));

      setAnalisis({
        conservadas: conservadas.map(({ id, nombre, estado }) => ({ id, nombre, estado })),
        aBorrar: detalle.sort((a, b) => b.reservas - a.reservas),
        totales: {
          sedes: locsBorrar.length,
          reservas: resBorrar.length,
          formularios: frmBorrar.length,
        },
        sinEmpresa,
        docs: {
          companies: candidatas.map((c) => c.id),
          locations: locsBorrar.map((d) => d.id),
          reservations: resBorrar.map((d) => d.id),
          forms: frmBorrar.map((d) => d.id),
        },
        respaldo: {
          generado: new Date().toISOString(),
          conservadas: conservadas.map((c) => ({ id: c.id, nombre: c.nombre })),
          companies: candidatas.map((c) => ({ id: c.id, ...(serializable(c.datos) as object) })),
          locations: locsBorrar.map((d) => ({ id: d.id, ...(serializable(d.data()) as object) })),
          reservations: resBorrar.map((d) => ({ id: d.id, ...(serializable(d.data()) as object) })),
          forms: frmBorrar.map((d) => ({ id: d.id, ...(serializable(d.data()) as object) })),
        },
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAnalizando(false);
    }
  };

  const descargarRespaldo = () => {
    if (!analisis) return;
    const blob = new Blob([JSON.stringify(analisis.respaldo, null, 1)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `respaldo-negocios-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setDescargado(true);
  };

  const borrar = async () => {
    if (!analisis) return;
    setBorrando(true);
    setError(null);
    // El orden importa: primero lo que cuelga de la empresa, la empresa al final.
    const pasos: Array<[string, string[]]> = [
      ['reservations', analisis.docs.reservations],
      ['locations', analisis.docs.locations],
      ['forms', analisis.docs.forms],
      ['companies', analisis.docs.companies],
    ];
    const total = pasos.reduce((n, [, ids]) => n + ids.length, 0);
    let hechos = 0;
    const fallidos: Array<[string, string]> = [];

    // Con miles de documentos siempre falla alguno por causas pasajeras de red.
    // Sin reintentar quedarían huérfanos: comprobado en dev, un formulario se
    // resistió a la primera y se borró sin problema al segundo intento.
    const borrarUno = async (coleccion: string, id: string) => {
      for (let intento = 0; intento < 3; intento++) {
        try {
          await deleteDoc(doc(firebaseFirestore, coleccion, id));
          return true;
        } catch {
          if (intento < 2) await new Promise((r) => setTimeout(r, 400 * (intento + 1)));
        }
      }
      fallidos.push([coleccion, id]);
      return false;
    };

    try {
      for (const [coleccion, ids] of pasos) {
        setProgreso({ hechos, total, paso: coleccion });
        // De 25 en 25 en paralelo: rápido sin saturar la conexión.
        for (let i = 0; i < ids.length; i += 25) {
          await Promise.all(ids.slice(i, i + 25).map((id) => borrarUno(coleccion, id)));
          hechos += Math.min(25, ids.length - i);
          setProgreso({ hechos, total, paso: coleccion });
        }
      }

      // Comprobación propia: se vuelve a mirar si de verdad desapareció todo.
      setProgreso({ hechos: total, total, paso: 'comprobando' });
      const restantes: string[] = [];
      for (const [coleccion, ids] of pasos) {
        const vivos = await getDocs(collection(firebaseFirestore, coleccion));
        const quedan = ids.filter((id) => vivos.docs.some((d) => d.id === id));
        if (quedan.length) restantes.push(`${quedan.length} en ${coleccion}`);
      }

      setResultado(
        restantes.length
          ? `Se borraron ${total - fallidos.length} de ${total}, pero siguen ahí: ${restantes.join(', ')}. Vuelve a analizar y repite.`
          : `Listo: ${total} documentos borrados y comprobado que no queda ninguno. Lo activo sigue intacto.`
      );
      setAnalisis(null);
      setPalabra('');
      setDescargado(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBorrando(false);
    }
  };

  if (!esSuperadmin) {
    return (
      <Box maxW='560px' mx='auto' mt={16} px={4}>
        <Alert status='error' borderRadius='md'>
          <AlertIcon />
          <Box>
            <Text fontWeight='semibold'>Esta pantalla es solo del superadministrador</Text>
            <Text fontSize='sm'>
              Entra con la cuenta de Sontickets para poder usarla.
            </Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  const puedeBorrar = Boolean(analisis) && descargado && palabra === PALABRA && !borrando;

  return (
    <Box maxW='980px' mx='auto' px={{ base: 4, md: 6 }} py={6}>
      <HStack spacing={3} mb={1}>
        <Icon as={FiTrash2} boxSize={5} color='red.500' />
        <Text fontSize='xl' fontWeight='bold'>
          Limpiar negocios que ya no operan
        </Text>
      </HStack>
      <Text fontSize='sm' color='gray.600' mb={5}>
        Borra las empresas inactivas con sus sedes, reservas y formularios. Lo que está activo
        no se toca nunca.
      </Text>

      <Alert status='info' borderRadius='md' mb={5} alignItems='flex-start'>
        <AlertIcon />
        <Box fontSize='sm'>
          <Text>
            Firestore guarda <b>7 días de recuperación continua</b>: si algo sale mal, se puede
            volver a cualquier instante anterior al borrado. Aun así, descarga el respaldo: es
            la copia que queda para siempre.
          </Text>
        </Box>
      </Alert>

      {error && (
        <Alert status='error' borderRadius='md' mb={4}>
          <AlertIcon />
          <Text fontSize='sm'>{error}</Text>
        </Alert>
      )}
      {resultado && (
        <Alert status='success' borderRadius='md' mb={4}>
          <AlertIcon />
          <Text fontSize='sm'>{resultado}</Text>
        </Alert>
      )}

      {/* Paso 1 */}
      <Button
        leftIcon={<FiSearch />}
        colorScheme='blue'
        onClick={analizar}
        isLoading={analizando}
        loadingText='Analizando…'
        mb={5}
      >
        1 · Analizar qué se borraría
      </Button>

      {analisis && (
        <>
          <Box borderWidth='1px' borderColor='green.200' bg='green.50' borderRadius='md' p={4} mb={5}>
            <HStack spacing={2} mb={1}>
              <Icon as={FiCheckCircle} color='green.500' />
              <Text fontWeight='semibold' fontSize='sm'>
                Se conserva intacto
              </Text>
            </HStack>
            {analisis.conservadas.map((c) => (
              <Text key={c.id} fontSize='sm' color='gray.700'>
                {c.nombre} <Badge colorScheme='green'>activo</Badge>
              </Text>
            ))}
            {analisis.sinEmpresa > 0 && (
              <Text fontSize='xs' color='gray.600' mt={2}>
                Y {analisis.sinEmpresa} reservas cuya empresa no se puede identificar: tampoco se
                tocan.
              </Text>
            )}
          </Box>

          <TableContainer borderWidth='1px' borderRadius='md' mb={4}>
            <Table size='sm'>
              <Thead bg='gray.50'>
                <Tr>
                  <Th>Negocio a borrar</Th>
                  <Th isNumeric>Sedes</Th>
                  <Th isNumeric>Reservas</Th>
                  <Th isNumeric>Formularios</Th>
                </Tr>
              </Thead>
              <Tbody>
                {analisis.aBorrar.map((c) => (
                  <Tr key={c.id}>
                    <Td>
                      {c.nombre}{' '}
                      <Badge colorScheme='gray' fontSize='0.6rem'>
                        {c.estado}
                      </Badge>
                    </Td>
                    <Td isNumeric>{c.sedes}</Td>
                    <Td isNumeric fontWeight={c.reservas > 500 ? 'bold' : 'normal'}>
                      {c.reservas.toLocaleString('es')}
                    </Td>
                    <Td isNumeric>{c.formularios}</Td>
                  </Tr>
                ))}
                <Tr bg='red.50'>
                  <Td fontWeight='bold'>TOTAL</Td>
                  <Td isNumeric fontWeight='bold'>
                    {analisis.totales.sedes}
                  </Td>
                  <Td isNumeric fontWeight='bold' color='red.600'>
                    {analisis.totales.reservas.toLocaleString('es')}
                  </Td>
                  <Td isNumeric fontWeight='bold'>
                    {analisis.totales.formularios}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>

          <Alert status='warning' borderRadius='md' mb={5} alignItems='flex-start'>
            <AlertIcon as={FiAlertTriangle} />
            <Text fontSize='sm'>
              Son datos reales de clientes: nombres, correos, teléfonos y documentos de
              identidad.
            </Text>
          </Alert>

          <Divider mb={5} />

          {/* Paso 2 */}
          <VStack align='stretch' spacing={4}>
            <Flex gap={3} align='center' wrap='wrap'>
              <Button
                leftIcon={<FiDownload />}
                colorScheme={descargado ? 'green' : 'orange'}
                variant={descargado ? 'outline' : 'solid'}
                onClick={descargarRespaldo}
              >
                2 · Descargar el respaldo
              </Button>
              {descargado ? (
                <HStack color='green.600' fontSize='sm'>
                  <Icon as={FiCheckCircle} />
                  <Text>Respaldo descargado. Guárdalo en un sitio seguro.</Text>
                </HStack>
              ) : (
                <Text fontSize='sm' color='gray.500'>
                  Obligatorio antes de poder borrar.
                </Text>
              )}
            </Flex>

            {/* Paso 3 */}
            <Box>
              <Text fontSize='sm' mb={2}>
                3 · Escribe <Code fontWeight='bold'>{PALABRA}</Code> para confirmar:
              </Text>
              <HStack>
                <Input
                  maxW='200px'
                  size='sm'
                  placeholder={PALABRA}
                  value={palabra}
                  isDisabled={!descargado || borrando}
                  onChange={(e) => setPalabra(e.target.value.toUpperCase())}
                />
                <Button
                  leftIcon={<FiTrash2 />}
                  colorScheme='red'
                  size='sm'
                  isDisabled={!puedeBorrar}
                  isLoading={borrando}
                  loadingText='Borrando…'
                  onClick={borrar}
                >
                  Borrar definitivamente
                </Button>
              </HStack>
            </Box>

            {borrando && (
              <Box>
                <Progress
                  value={progreso.total ? (progreso.hechos / progreso.total) * 100 : 0}
                  size='sm'
                  colorScheme='red'
                  borderRadius='full'
                  hasStripe
                  isAnimated
                />
                <Text fontSize='xs' color='gray.600' mt={1}>
                  {progreso.paso}: {progreso.hechos.toLocaleString('es')} de{' '}
                  {progreso.total.toLocaleString('es')} · no cierres esta ventana
                </Text>
              </Box>
            )}
          </VStack>
        </>
      )}
    </Box>
  );
};

export default LimpiarNegocios;
